import { useEffect, useMemo, useState } from "react";
import { Loader2, Package, AlertTriangle, CheckCircle2, MapPin, Info } from "lucide-react";
import Modal from "../Modal";
import api from "../../lib/api";

/**
 * EquipmentExtractModal — promotes invoice line items to physical
 * equipment rows on a chosen site. The chip on the invoice row opens
 * this modal; the user picks a site, eyeballs the lines (freight /
 * labour / discount auto-untoggled), edits serials + service intervals,
 * and clicks Apply. Re-opening on the same doc shows already-extracted
 * rows so the user can update them without dupes (the backend dedupes
 * via source_sales_document_id + source_line_index).
 */
export default function EquipmentExtractModal({
  open,
  onClose,
  documentId,
  documentLabel,
  customerId,
  defaultSiteId = null,
  onApplied,
}) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [doc, setDoc] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(defaultSiteId);
  const [lines, setLines] = useState([]);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  // Load preview whenever modal opens or site selection changes (site
  // change refreshes the "already extracted at this site" hints).
  useEffect(() => {
    if (!open || !documentId) return undefined;
    let alive = true;
    const run = async () => {
      setLoading(true);
      setErr("");
      setResult(null);
      const qs = siteId ? `?site_id=${encodeURIComponent(siteId)}` : "";
      try {
        const { data } = await api.get(
          `/sales-documents/${documentId}/equipment/preview${qs}`,
        );
        if (!alive) return;
        setDoc(data.doc);
        setSites(data.sites || []);
        if (!siteId) {
          const pre = data.doc?.site_id
            || (data.sites || []).find((s) => s.id === defaultSiteId)?.id
            || (data.sites || [])[0]?.id
            || null;
          setSiteId(pre);
        }
        setLines(
          (data.lines || []).map((ln) => ({
            ...ln,
            include: !!ln.default_include,
            serial: ln.existing_serial || "",
            is_service_item: !!ln.existing_is_service_item,
            interval_days: ln.existing_interval_days || null,
            install_date: "",
            notes: "",
          })),
        );
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.detail || e?.message || "Could not load lines.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentId, siteId]);

  const counts = useMemo(() => {
    const selected = lines.filter((l) => l.include).length;
    const dupes = lines.filter((l) => l.include && l.existing_equipment_id).length;
    const services = lines.filter((l) => l.include && l.is_service_item).length;
    return { selected, dupes, services };
  }, [lines]);

  const toggleAll = (val) => {
    setLines((prev) => prev.map((l) => ({
      ...l,
      include: val ? !l.is_likely_non_equipment : false,
    })));
  };

  const patchLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const handleApply = async () => {
    if (!siteId) { setErr("Pick a site first."); return; }
    const picks = lines.filter((l) => l.include);
    if (picks.length === 0) { setErr("Pick at least one line."); return; }
    setApplying(true);
    setErr("");
    try {
      const body = {
        site_id: siteId,
        lines: picks.map((l) => ({
          line_index: l.line_index,
          name: l.name,
          quantity: l.quantity || 1,
          unit: l.unit || "pcs",
          serial: l.serial || null,
          install_date: l.install_date || null,
          notes: l.notes || null,
          inventory_item_id: l.matched_inventory_id || null,
          is_service_item: !!l.is_service_item,
          interval_days: l.is_service_item && l.interval_days ? Number(l.interval_days) : null,
        })),
      };
      const { data } = await api.post(
        `/sales-documents/${documentId}/equipment/apply`,
        body,
      );
      setResult(data);
      await onApplied?.(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Apply failed.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-[#1FB6C1]" />
          <span>Extract equipment from {documentLabel || "invoice"}</span>
        </div>
      }
      testId="equipment-extract-modal"
      maxWidth="max-w-3xl"
      footer={
        <div className="flex items-center justify-between gap-3 w-full flex-wrap">
          <div className="text-[11px] text-[#647087]">
            {counts.selected} line{counts.selected === 1 ? "" : "s"} selected
            {counts.services > 0 && ` · ${counts.services} as service item${counts.services === 1 ? "" : "s"}`}
            {counts.dupes > 0 && ` · ${counts.dupes} will update an existing row`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="equipment-extract-cancel"
            >Cancel</button>
            <button
              type="button"
              onClick={handleApply}
              disabled={applying || loading || counts.selected === 0}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="equipment-extract-apply"
            >
              {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
              {applying ? "Applying…" : `Apply ${counts.selected} to site`}
            </button>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 text-[#a0aabf] text-sm py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading lines…
        </div>
      ) : err && !lines.length ? (
        <div className="rounded border border-rose-400/40 bg-rose-500/10 p-3 text-sm text-rose-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{err}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Site picker */}
          <div className="rounded border border-white/10 bg-white/[0.02] p-3 space-y-2">
            <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#647087] font-bold">
              <MapPin className="w-3 h-3" /> Target site
            </label>
            {sites.length === 0 ? (
              <div className="text-xs text-rose-300">
                This customer has no sites. Create a site before extracting equipment.
              </div>
            ) : (
              <select
                value={siteId || ""}
                onChange={(e) => setSiteId(e.target.value || null)}
                className="w-full bg-[#0a0b0e] border border-white/10 rounded px-2 py-1.5 text-sm"
                data-testid="equipment-extract-site"
              >
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.address || s.id.slice(0, 8)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Lines table */}
          <div className="rounded border border-white/10">
            <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
                {lines.length} line{lines.length === 1 ? "" : "s"} from document
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => toggleAll(true)}
                  className="text-[10px] text-[#1FB6C1] hover:underline"
                  data-testid="equipment-extract-select-all"
                >Select likely-equipment</button>
                <span className="text-[10px] text-[#647087]">·</span>
                <button
                  type="button"
                  onClick={() => toggleAll(false)}
                  className="text-[10px] text-[#647087] hover:underline"
                  data-testid="equipment-extract-clear"
                >Clear</button>
              </div>
            </div>
            {lines.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-[#647087] italic">
                No line items on this document. Re-run OCR or add them manually first.
              </div>
            ) : (
              <div className="divide-y divide-white/5 max-h-[420px] overflow-y-auto">
                {lines.map((ln, idx) => (
                  <LineRow
                    key={`line-${ln.line_index}`}
                    line={ln}
                    onChange={(patch) => patchLine(idx, patch)}
                  />
                ))}
              </div>
            )}
          </div>

          {result && (
            <div className="rounded border border-emerald-400/40 bg-emerald-500/10 p-3 text-sm text-emerald-200 flex items-start gap-2" data-testid="equipment-extract-result">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                Created <b>{result.created_count}</b> · Updated <b>{result.updated_count}</b>{" "}
                equipment row{(result.created_count + result.updated_count) === 1 ? "" : "s"}.
              </div>
            </div>
          )}

          {err && lines.length > 0 && (
            <div className="rounded border border-rose-400/40 bg-rose-500/10 p-2 text-xs text-rose-200 flex items-start gap-2">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {err}
            </div>
          )}

          <div className="text-[10px] text-[#647087] flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            Equipment is tagged with this document for traceability. Use the
            chip&apos;s &quot;Unsync&quot; option later to remove every row created from
            this doc in one click.
          </div>
        </div>
      )}
    </Modal>
  );
}

function LineRow({ line, onChange }) {
  const muted = line.is_likely_non_equipment && !line.include;
  return (
    <div
      className={`px-3 py-2 transition-colors ${muted ? "opacity-60" : ""}`}
      data-testid={`equipment-extract-line-${line.line_index}`}
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={!!line.include}
          onChange={(e) => onChange({ include: e.target.checked })}
          className="w-4 h-4 accent-[#1FB6C1] mt-1 shrink-0"
          data-testid={`equipment-extract-line-${line.line_index}-include`}
        />
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium truncate">
              {line.name}
            </div>
            {line.sku && (
              <span className="text-[10px] font-mono text-[#a0aabf] bg-white/5 px-1.5 py-0.5 rounded">
                {line.sku}
              </span>
            )}
            {line.matched_inventory_id && (
              <span className="text-[10px] font-mono text-cyan-300 bg-cyan-500/10 border border-cyan-400/30 px-1.5 py-0.5 rounded">
                {line.matched_name || "matched"}
              </span>
            )}
            <span className="text-[10px] text-[#647087]">
              {line.quantity} {line.unit}
            </span>
            {line.is_likely_non_equipment && (
              <span className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-400/30 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                <AlertTriangle className="w-2.5 h-2.5" /> likely not equipment
              </span>
            )}
            {line.existing_equipment_id && (
              <span className="text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 px-1.5 py-0.5 rounded">
                will update existing
              </span>
            )}
          </div>

          {/* Edit fields — visible only when this row is included */}
          {line.include && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
              <label className="col-span-2 text-[10px] uppercase tracking-wider text-[#647087]">
                Serial #
                <input
                  type="text"
                  value={line.serial || ""}
                  onChange={(e) => onChange({ serial: e.target.value })}
                  placeholder="(optional)"
                  className="w-full bg-[#0a0b0e] border border-white/10 rounded px-1.5 py-1 text-xs text-white mt-0.5 normal-case tracking-normal"
                  data-testid={`equipment-extract-line-${line.line_index}-serial`}
                />
              </label>
              <label className="text-[10px] uppercase tracking-wider text-[#647087] flex flex-col">
                Service item?
                <button
                  type="button"
                  onClick={() => onChange({ is_service_item: !line.is_service_item })}
                  className={`mt-0.5 text-[10px] px-2 py-1 rounded border transition-colors ${
                    line.is_service_item
                      ? "border-emerald-400/50 bg-emerald-500/20 text-emerald-200"
                      : "border-white/10 bg-white/5 text-[#a0aabf]"
                  }`}
                  data-testid={`equipment-extract-line-${line.line_index}-isservice`}
                >
                  {line.is_service_item ? "Yes — schedule" : "No — one-off"}
                </button>
              </label>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">
                {line.is_service_item ? "Interval (days)" : "—"}
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={line.interval_days || ""}
                  onChange={(e) => onChange({ interval_days: e.target.value ? Number(e.target.value) : null })}
                  disabled={!line.is_service_item}
                  placeholder={line.is_service_item ? "90" : ""}
                  className="w-full bg-[#0a0b0e] border border-white/10 rounded px-1.5 py-1 text-xs text-white mt-0.5 disabled:opacity-40 normal-case tracking-normal"
                  data-testid={`equipment-extract-line-${line.line_index}-interval`}
                />
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
